import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Package, Star, Truck, CheckCircle2, RotateCw, MapPin, Pencil } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderCard = ({ order, onReorder }) => {
  const short = order.session_id.slice(-8);
  return (
    <li className="border border-off-white/10 bg-off-white/[0.02] p-5" data-testid={`order-card-${short}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/40">
            #{short} · {order.created_at ? new Date(order.created_at).toLocaleDateString() : ""}
          </p>
          <p className="font-serif-display italic text-2xl text-off-white mt-1">${order.amount?.toFixed(2)}</p>
        </div>
        <div className="flex items-center gap-2">
          {order.fulfilled ? (
            <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1 bg-rose/20 text-rose border border-rose/40 flex items-center gap-1">
              <Truck size={12} /> SHIPPED
            </span>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.25em] px-3 py-1 border border-off-white/20 text-off-white/70 flex items-center gap-1">
              <Package size={12} /> IN PROGRESS
            </span>
          )}
        </div>
      </div>
      <ul className="mt-4 space-y-1">
        {order.items?.map((it, i) => (
          <li key={i} className="text-sm text-off-white/85 flex justify-between">
            <span>{it.qty} × {it.name}</span>
            <span className="font-mono text-off-white/50">${it.line_total?.toFixed(2)}</span>
          </li>
        ))}
      </ul>
      {order.tracking_number && (
        <div className="mt-3 border-t border-off-white/10 pt-3 text-sm">
          <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">TRACKING · </span>
          <span className="text-off-white">{order.tracking_carrier || ""} {order.tracking_number}</span>
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onReorder(order)}
          className="h-9 px-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-off-white/20 hover:border-rose hover:text-rose text-off-white/80 transition-colors-only"
          data-testid={`reorder-btn-${short}`}
        >
          <RotateCw size={12} /> REORDER
        </button>
      </div>
    </li>
  );
};

const Account = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();
  const [orders, setOrders] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/account" } }); return; }
    (async () => {
      setDataLoading(true);
      try {
        const [o, r] = await Promise.all([
          axios.get(`${API}/me/orders`, { withCredentials: true }),
          axios.get(`${API}/me/reviews`, { withCredentials: true }),
        ]);
        setOrders(o.data); setMyReviews(r.data);
      } catch (_e) { /* noop */ }
      finally { setDataLoading(false); }
    })();
  }, [user, loading, navigate]);

  const handleReorder = (order) => {
    order.items?.forEach((it) => {
      if (it.productId && it.unit_price > 0) {
        cart.addItem({ id: it.productId, name: it.name, price: it.unit_price, image: "" }, it.qty);
      }
    });
    toast.success("Items added to your cart");
  };

  if (loading || dataLoading) {
    return (
      <main className="min-h-screen bg-charcoal flex items-center justify-center">
        <div className="flex items-center gap-3 text-off-white/60">
          <Loader2 size={20} className="animate-spin text-rose" />
          <span className="font-mono text-[11px] tracking-[0.25em]">LOADING YOUR ACCOUNT…</span>
        </div>
      </main>
    );
  }

  if (!user) return null;

  return (
    <main className="min-h-screen bg-charcoal pt-8" data-testid="account-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 py-8 border-b border-off-white/10">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// YOUR ACCOUNT</p>
            <h1 className="font-serif-display italic text-4xl md:text-6xl text-off-white leading-[0.95]" data-testid="account-greeting">
              Hi, {user.name || user.email.split("@")[0]}.
            </h1>
            <p className="text-off-white/60 mt-2 text-sm">{user.email}</p>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="h-11 px-4 border border-off-white/20 hover:border-rose hover:text-rose flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-off-white/80 transition-colors-only"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> SIGN OUT
          </button>
        </div>

        {/* Shipping address */}
        <section className="py-8 border-b border-off-white/10" data-testid="account-address">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2 flex items-center gap-2"><MapPin size={12} /> SHIPPING ADDRESS</p>
              {user.address && user.address.line1 ? (
                <div className="text-off-white/85 leading-relaxed text-sm">
                  <p className="text-off-white">{user.name}</p>
                  <p>{user.address.line1}{user.address.line2 ? `, ${user.address.line2}` : ""}</p>
                  <p>{[user.address.city, user.address.state, user.address.postal_code].filter(Boolean).join(", ")}</p>
                  <p className="text-off-white/60">{user.address.country}</p>
                </div>
              ) : (
                <p className="text-off-white/60 text-sm">No address on file yet.</p>
              )}
            </div>
            <Link
              to="/complete-profile"
              className="h-10 px-4 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-off-white/20 hover:border-rose hover:text-rose text-off-white/80 transition-colors-only"
              data-testid="edit-address-link"
            >
              <Pencil size={12} /> {user.address?.line1 ? "EDIT" : "ADD"} ADDRESS
            </Link>
          </div>
        </section>

        {/* Orders */}
        <section className="py-10 border-b border-off-white/10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// ORDER HISTORY</p>
              <h2 className="font-serif-display italic text-3xl text-off-white">Your orders.</h2>
            </div>
            <Link to="/shop" className="font-mono text-[11px] tracking-[0.25em] text-off-white/60 hover:text-rose transition-colors-only">SHOP AGAIN →</Link>
          </div>
          {orders.length === 0 ? (
            <div className="border border-off-white/10 p-10 text-center text-off-white/50">
              <Package className="mx-auto text-off-white/40 mb-3" size={22} />
              <p className="font-mono text-[10px] tracking-[0.3em]">// NO ORDERS YET</p>
              <Link to="/shop" className="mt-4 inline-block font-mono text-[11px] tracking-[0.25em] text-rose">START SHOPPING →</Link>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="orders-list">
              {orders.map((o) => <OrderCard key={o.session_id} order={o} onReorder={handleReorder} />)}
            </ul>
          )}
        </section>

        {/* My reviews */}
        <section className="py-10">
          <div className="mb-6">
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// YOUR REVIEWS</p>
            <h2 className="font-serif-display italic text-3xl text-off-white">Reviews you've left.</h2>
          </div>
          {myReviews.length === 0 ? (
            <div className="border border-off-white/10 p-10 text-center text-off-white/50">
              <Star className="mx-auto text-off-white/40 mb-3" size={22} />
              <p className="font-mono text-[10px] tracking-[0.3em]">// NO REVIEWS YET</p>
              <p className="text-sm mt-2">Verified buyers can review any product they&apos;ve ordered.</p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="my-reviews-list">
              {myReviews.map((r) => (
                <li key={r.review_id} className="border border-off-white/10 bg-off-white/[0.02] p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-rose">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={14} fill={i < r.rating ? "currentColor" : "none"} className={i < r.rating ? "" : "text-off-white/20"} />
                      ))}
                    </div>
                    <span className={`font-mono text-[10px] tracking-[0.25em] px-2 py-0.5 border ${
                      r.status === "approved" ? "border-rose/40 text-rose"
                      : r.status === "rejected" ? "border-off-white/20 text-off-white/40"
                      : "border-off-white/20 text-off-white/60"
                    }`}>{r.status.toUpperCase()}</span>
                  </div>
                  {r.title && <p className="mt-2 font-serif-display text-lg text-off-white">{r.title}</p>}
                  <p className="mt-1 text-off-white/70 text-sm">{r.body}</p>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-off-white/40">FOR {r.productId.toUpperCase()}</p>
                </li>
              ))}
            </ul>
          )}
          {orders.length > 0 && (
            <div className="mt-6 border border-off-white/10 p-5 text-off-white/70 text-sm">
              <CheckCircle2 className="inline mr-2 text-rose" size={14} />
              Want to leave a review? Head to any product page you&apos;ve ordered — reviews open up automatically for verified buyers.
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default Account;
